import { useState, useMemo, useEffect } from "react";
import { Modal } from "@mantine/core";
import {
  BiCheckCircle,
  BiInfoCircle,
  BiLoaderAlt,
  BiXCircle,
} from "react-icons/bi";

const EmailListModal = ({ messages = [], modalOpen, setModalOpen }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const perPage = 20;

  const totalPages = useMemo(
    () => Math.ceil(messages.length / perPage),
    [messages]
  );

  const currentMessages = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    const end = start + perPage;
    return messages.slice(start, end);
  }, [currentPage, messages]);

  const close = () => setModalOpen(false);

  const changePage = (newPage) => {
    if (newPage === currentPage) return;
    setLoading(true);

    setTimeout(() => {
      setCurrentPage(newPage);
      setLoading(false);
    }, 500);
  };

  const goNext = () => changePage(Math.min(currentPage + 1, totalPages));
  const goPrev = () => changePage(Math.max(currentPage - 1, 1));

  useEffect(() => {
    if (modalOpen) {
      setLoading(true);
      setCurrentPage(1);

      const timer = setTimeout(() => setLoading(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [modalOpen]);

  return (
    <Modal
      centered
      opened={modalOpen}
      onClose={close}
      fullScreen
      withCloseButton={false}
      zIndex={999}
      overlayProps={{ className: "email-modal-overlay" }}
      classNames={{
        content: "email-modal-content",
      }}
    >
      {loading ? (
        <div className='flex justify-center items-center h-[400px] text-gray-500'>
          <BiLoaderAlt className='animate-spin text-4xl text-primary dark:text-white' />
        </div>
      ) : (
        <>
          <div className=' w-[800px] m-auto mb-5 flex items-center justify-between'>
            <h1 className='text-xl font-semibold dark:text-gray-100'>
              Emails ({messages?.length})
            </h1>
            <h1 className='text-xl cursor-pointer' onClick={close}>
              X
            </h1>
          </div>
          <div className='overflow-x-auto'>
            <table className='m-auto w-[800px]  border-collapse border border-gray-300 dark:border-gray-600'>
              <thead>
                <tr className='bg-gray-100  dark:bg-gray-900'>
                  <th className='border p-2 text-left dark:border-gray-600'>
                    Email
                  </th>
                  <th className='border p-2 text-left dark:border-gray-600'>
                    Subject
                  </th>
                  <th className='border p-2 text-left dark:border-gray-600'>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className='text-sm'>
                {currentMessages.length ? (
                  currentMessages.map((msg, index) => (
                    <tr
                      key={index}
                      className='hover:bg-gray-50 dark:hover:bg-gray-800'
                    >
                      <td className='border p-2 dark:border-gray-600 '>
                        {msg.email}
                      </td>
                      <td className='border p-2 dark:border-gray-600 '>
                        {msg.subject}
                      </td>
                      <td className='border p-2  dark:border-gray-600 flex items-center space-x-2'>
                        {msg.status === "Delivered" && (
                          <BiCheckCircle className='text-green-500' />
                        )}
                        {msg.status === "Failed" && (
                          <BiXCircle className='text-red-500' />
                        )}
                        {msg.status === "Opt-Out" && (
                          <BiInfoCircle className='text-yellow-500' />
                        )}
                        <span>{msg.status || "-"}</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className='border p-2 text-center' colSpan={3}>
                      No messages available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {messages.length > perPage && (
            <div className='mt-4 m-auto w-[250px] flex justify-between items-center'>
              <button
                onClick={goPrev}
                disabled={currentPage === 1}
                className='border text-black dark:text-white px-4 py-2 text-sm text-gray-800 hover:bg-red-600 hover:border-red-600 disabled:opacity-50'
              >
                Previous
              </button>

              <span className='text-sm'>
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={goNext}
                disabled={currentPage === totalPages}
                className='border text-black dark:text-white px-4 py-2 text-sm text-gray-800 hover:bg-red-600 hover:border-red-600 disabled:opacity-50'
              >
                Next
              </button>
            </div>
          )}

          <div className='mt-4 flex justify-end'>
            <button
              onClick={close}
              className='rounded-lg bg-red-600 px-4 py-2 text-sm text-gray-100 hover:bg-red-800'
            >
              Close
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default EmailListModal;
